const EventEmitter = require('events');
const { nanoid } = require('nanoid');
const { Base64 } = require('js-base64');
const RpcError = require('./RpcError');

const TIMEOUT = 10 * 60 * 1000; // 10 Minutes
const compose = (...fns) => {
  return (...args) => {
    for (const fn of fns) {
      fn(...args);
    }
  }
}
      


class BrokerRpc {

  constructor(brokerClient, gatewaySite, gatewayCode, userName) {
    this.gatewaySite = gatewaySite;
    this.gatewayCode = gatewayCode;
    this.userName = userName;
    this.targetQueue = `msi.meerkat.Gateway.${gatewaySite}.${gatewayCode}`;
    this.replyQueue = `msi.meerkat.User.${userName}`;
    this.broker = brokerClient;
    this.corrCounter = 0;
    this.session = nanoid(8);
    this.mailbox = new EventEmitter();

    this.broker.subscribe(this.replyQueue, (msg) => {
      this._onReplyMessage(msg)
    }, {
      selector: `session = '${this.session}'`,
      ack: 'client-individual'
    });

    this.broker.onStompError = compose(this.broker.onStompError, (frame) => {
      const msg = frame.headers.message || frame.body;
      this.mailbox.emit('error', new Error(msg));
    });
  }

  tryParseStatus(str, defaultStatus=500) {
    try {
      return parseInt(str)
    } catch (error) {
      return defaultStatus;
    }
  }

  _onReplyMessage(msg) {
    msg.ack();

    const correlationId = msg.headers['correlation-id'];
    if(!correlationId) return;

    const type = msg.headers.type || 'reply';
    const encoding = msg.headers.encoding || 'binary';

    if (type === 'error') {
      const errName = msg.headers['error-type'] || 'UnkownError';
      const expose = msg.headers['error-expose'] === 'true';
      const status = this.tryParseStatus(msg.headers['error-status']);
      const fullErrName = `${this.userName} -> ${this.gatewayCode}@${this.gatewaySite} ${errName}`;
      const err = new RpcError(msg.body, fullErrName, expose, status);
      this.mailbox.emit(correlationId, err);
    } else {
      let reply = msg.body
      if (encoding === 'base64') {
        try {
          reply = Base64.toUint8Array(reply)
        } catch (err) {
          this.mailbox.emit(correlationId, err);
          return;
        }
      }
      this.mailbox.emit(correlationId, null, reply);
    }
    
  }

  _sendRequest(service, method, data, corrId) {
    const methodName = `/${service}/${method.name}`;

    let body = data;
    let now = Date.now()
    const headers = {
      method: methodName,
      session: this.session,
      timestamp: now,
      expires: now + TIMEOUT,
      'correlation-id': corrId,
      'reply-to': this.replyQueue
    }

    headers.encoding = 'base64';
    if (body && body.length > 0) {
      body = Base64.fromUint8Array(body);
    }

    this.broker.publish({
      destination: this.targetQueue,
      body,
      headers
    });
  }

  _getReply(correlationId) {
    return new Promise((resolve, reject) => {
      let timeout;
      const onReply = (err, reply) => {
        if (timeout) clearTimeout(timeout);
        this.mailbox.off('error', onReply);
        this.mailbox.off(correlationId, onReply);
        
        if (err) reject(err);
        else resolve(reply);
      }
      this.mailbox.once(correlationId, onReply);
      this.mailbox.once('error', onReply);

      timeout = setTimeout(() => {
        this.mailbox.off('error', onReply);
        this.mailbox.off(correlationId, onReply);
        const fullErrName = `${this.userName} -> ${this.gatewayCode}@${this.gatewaySite} GatewayTimeoutError`;
        const err = new RpcError('Gateway did not reply', fullErrName, true, 503)
        reject(err);
      }, TIMEOUT)
    });
  }

  async _unaryCall(service, method, requestData) {
    const corrId = nanoid(8);
    this._sendRequest(service, method, requestData, corrId);
    return this._getReply(corrId);
  }

  _rpc(service, method, requestData, callback) {
    this._unaryCall(service, method, requestData)
      .then(reply => callback(null, reply))
      .catch(err => callback(err))
  }

  rpc(service) {
    return this._rpc.bind(this, service);
  }

}

module.exports = BrokerRpc;
